/* __next_internal_client_entry_do_not_use__ ScrollButton,Default,SHARED_PLACEHOLDER_VARIANTS auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
// Global
import { useState, useEffect } from 'react';
import { tv } from 'tailwind-variants';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { findComponent, getParentSectionFields } from 'lib/utils/object-utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { TabContextProvider, useTab } from 'helpers/Context/TabContext';
import { SvgIcon } from 'helpers/SvgIcon';
import { useScrollableTabs } from 'lib/hooks/useScrollableTabs';
import useDictionary from 'lib/hooks/useDictionary';
import useIsEditing from 'lib/hooks/useIsEditing';
import { getTestProps } from 'lib/testing/utils';
import { createTabClickGTMEvent } from 'lib/utils/gtm-utils';
import { sendGTMEvent } from '@next/third-parties/google';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
export const ScrollButton = ({ direction, onClick, tabListId })=>{
    const { tabArrows } = TAILWIND_VARIANTS();
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    };
    return /*#__PURE__*/ _jsx("button", {
        className: tabArrows({
            left: direction === 'left',
            right: direction === 'right'
        }),
        onClick: onClick,
        onKeyDown: handleKeyDown,
        "aria-label": `Scroll tabs ${direction}`,
        "aria-controls": tabListId,
        tabIndex: 0,
        children: /*#__PURE__*/ _jsx(SvgIcon, {
            icon: `arrow-dash-${direction}-thin`,
            size: "s",
            viewBox: '0 0 20 20',
            fill: "currentColor"
        })
    });
};
const ServiceTabContent = (props)=>{
    const { DynamicPlaceholderId, RenderingIdentifier } = props?.params || {};
    const { activeTab, setActiveTab } = useTab();
    const { getDictionaryValue } = useDictionary();
    const isEditing = useIsEditing();
    const { containerRef, isScrollable, isLeftArrowVisible, isRightArrowVisible, handleScroll } = useScrollableTabs();
    const phKey = `servicetab-${DynamicPlaceholderId}`;
    const tabItems = findComponent(props.rendering, 'ServiceTabItem') || [];
    const hasMultipleItems = tabItems.length > 1;
    const shouldShowTabs = hasMultipleItems || isEditing;
    // Create unique IDs for accessibility using component instance
    // Use useState with empty string to prevent hydration mismatch
    const baseId = RenderingIdentifier || props?.rendering?.uid || 'default';
    const [tabListId, setTabListId] = useState(`tab-list-${baseId}`);
    // Generate unique ID only on client side to prevent hydration mismatch
    useEffect(()=>{
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 5);
        setTabListId(`tab-list-${baseId}-${timestamp}-${random}`);
    }, [
        baseId
    ]);
    // Get Sitecore context for component searching
    const { sitecoreContext } = useSitecoreContext();
    const fields = getParentSectionFields(sitecoreContext ?? {}, props?.rendering?.uid || '');
    const sectionTitle = fields?.title?.value ?? '';
    const { base, tabsContainerWrapper, tabsWrapper, tabLinkWrapper, tabLink, tabHeader, tabImage, tabText, contentWrapper } = TAILWIND_VARIANTS();
    const { imagePlaceholder } = SHARED_PLACEHOLDER_VARIANTS({
        size: 'fixed',
        isActive: false
    });
    return /*#__PURE__*/ _jsx("section", {
        className: base(),
        "data-component": "authorable/shared/lists/ServiceTabs",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-tab-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsxs("div", {
            className: tabsContainerWrapper(),
            children: [
                shouldShowTabs && /*#__PURE__*/ _jsxs("div", {
                    className: "relative",
                    children: [
                        isScrollable && isLeftArrowVisible && /*#__PURE__*/ _jsx(ScrollButton, {
                            direction: "left",
                            onClick: ()=>handleScroll('left'),
                            tabListId: tabListId
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: tabsWrapper(),
                            ref: containerRef,
                            role: "tablist",
                            id: tabListId,
                            "aria-label": "Service tab navigation",
                            ...getTestProps(`tab-ul`),
                            children: tabItems.map((item, index)=>{
                                const isActive = activeTab === item.uid;
                                // Support both image/title and tabIcon/tabTitle field names
                                const imageField = item.fields?.image || item.fields?.tabIcon;
                                const titleField = item.fields?.title || item.fields?.tabTitle;
                                return /*#__PURE__*/ _jsx("li", {
                                    className: tabLinkWrapper({
                                        isActive
                                    }),
                                    ...getTestProps(`tab-content-${index + 1}`),
                                    role: "presentation",
                                    children: /*#__PURE__*/ _jsx("button", {
                                        className: tabLink({
                                            isActive,
                                            disabled: false
                                        }),
                                        onClick: ()=>{
                                            setActiveTab(item.uid || '');
                                            // Create and send GTM event
                                            const gtmEvent = createTabClickGTMEvent({
                                                event: 'tab_click',
                                                header: sectionTitle,
                                                ctaText: titleField?.value ?? '',
                                                componentName: 'Tabbed Cards'
                                            });
                                            sendGTMEvent({
                                                ...gtmEvent
                                            });
                                        },
                                        id: `tab-${item.uid}`,
                                        role: "tab",
                                        "aria-controls": `tab-content-${item.uid}`,
                                        "aria-label": titleField?.value || `Tab ${index + 1}`,
                                        "aria-selected": isActive,
                                        ...getTestProps(`tab-link-${index + 1}`),
                                        children: /*#__PURE__*/ _jsxs("div", {
                                            className: tabHeader(),
                                            children: [
                                                isEditing ? /*#__PURE__*/ _jsx("div", {
                                                    className: "max-w-14 max-h-14",
                                                    children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                                        field: imageField,
                                                        className: 'rounded-border-radius-radius-2 overflow-hidden object-cover',
                                                        ...getTestProps(`tab-image-${index + 1}`)
                                                    })
                                                }) : /*#__PURE__*/ _jsx(_Fragment, {
                                                    children: imageField?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                                                        field: imageField,
                                                        className: tabImage(),
                                                        ...getTestProps(`tab-image-${index + 1}`)
                                                    }) : /*#__PURE__*/ _jsx("div", {
                                                        className: tabImage(),
                                                        children: /*#__PURE__*/ _jsx("div", {
                                                            className: imagePlaceholder(),
                                                            children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                                                field: {
                                                                    value: {
                                                                        src: '/assets/profile_placeholder.png',
                                                                        alt: 'No image'
                                                                    }
                                                                },
                                                                className: tabImage(),
                                                                ...getTestProps(`related-profile-image-placeholder`)
                                                            })
                                                        })
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: tabText(),
                                                    children: isEditing ? /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                        field: titleField,
                                                        ...getTestProps(`tab-title-${index + 1}`)
                                                    }) : /*#__PURE__*/ _jsx(_Fragment, {
                                                        children: titleField?.value ? /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                            field: titleField,
                                                            ...getTestProps(`tab-title-${index + 1}`)
                                                        }) : /*#__PURE__*/ _jsx("div", {
                                                            className: SHARED_PLACEHOLDER_VARIANTS({
                                                                size: 'full',
                                                                isActive
                                                            }).titlePlaceholder(),
                                                            children: getDictionaryValue('NoTitle') || 'No Title'
                                                        })
                                                    })
                                                })
                                            ]
                                        })
                                    })
                                }, item.uid);
                            })
                        }),
                        isScrollable && isRightArrowVisible && /*#__PURE__*/ _jsx(ScrollButton, {
                            direction: "right",
                            onClick: ()=>handleScroll('right'),
                            tabListId: tabListId
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: contentWrapper(),
                    children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                        name: phKey,
                        rendering: props.rendering,
                        ...getTestProps(`tab-ph-${props?.rendering?.uid}`)
                    })
                })
            ]
        })
    });
};
const ServiceTabs = (props)=>{
    const tabItems = findComponent(props.rendering, 'ServiceTabItem') || [];
    return /*#__PURE__*/ _jsx(TabContextProvider, {
        tabs: tabItems,
        children: /*#__PURE__*/ _jsx(ServiceTabContent, {
            ...props
        })
    });
};
export const Default = withStandardComponentWrapper(ServiceTabs, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'w-full'
        ],
        tabsContainerWrapper: [
            'flex',
            'flex-col',
            'lg:flex-row',
            'gap-component-section-container-gutter',
            'lg:gap-spacing-spacing-24',
            'relative',
            'lg:items-start'
        ],
        tabsWrapper: [
            'relative',
            'flex',
            'flex-row',
            'lg:flex-col',
            'items-center',
            'lg:items-start',
            'justify-start',
            '-mr-spacing-spacing-16',
            'sm:-mr-spacing-spacing-20',
            'lg:-mr-component-section-container-padding-x',
            'lg:mr-0',
            'lg:mb-0',
            'gap-spacing-spacing-8',
            'sm:gap-spacing-spacing-12',
            'lg:gap-spacing-spacing-8',
            'overflow-x-auto',
            'lg:overflow-x-visible',
            'lg:overflow-y-visible',
            'scrollbar-width-none',
            'snap-x',
            'lg:snap-none',
            'min-h-24',
            'lg:min-h-0',
            'snap-mandatory',
            'lg:snap-none',
            '[-ms-overflow-style:none]',
            '[&::-webkit-scrollbar]:hidden',
            'p-1',
            'lg:p-0',
            'scroll-pl-1',
            'lg:scroll-pl-0',
            'w-full',
            'lg:w-[260px]',
            'lg:flex-shrink-0'
        ],
        tabLinkWrapper: [
            'flex-shrink-0',
            'snap-start',
            'snap-always',
            'lg:snap-none',
            'lg:w-full'
        ],
        tabArrows: [
            'absolute',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'z-10',
            'flex',
            'w-12',
            'h-12',
            'p-spacing-spacing-12',
            'items-center',
            'gap-spacing-spacing-8',
            'aspect-square',
            'rounded-general-border-radius-button',
            'opacity-80',
            'bg-component-button-primary-bg',
            'hover:opacity-100',
            'active:opacity-100',
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-offset-2',
            'disabled:opacity-50',
            'disabled:cursor-not-allowed',
            'lg:hidden'
        ],
        tabLink: [
            'flex',
            'items-center',
            'h-full',
            'lg:w-full',
            'gap-spacing-spacing-3',
            'lg:gap-spacing-spacing-16',
            'p-spacing-spacing-16',
            'text-left',
            'rounded-general-border-radius-button',
            'lg:rounded-border-radius-radius-2',
            'transition-all',
            'duration-200',
            'cursor-pointer',
            'focus:outline',
            'focus:outline-color-border-brand-2',
            'focus:outline-[3px]',
            'shadow-[0_0_8px_0_rgba(0,0,0,0.10)]',
            'min-w-[260px]',
            'lg:min-w-0'
        ],
        tabHeader: [
            'flex',
            'items-center',
            'gap-spacing-spacing-16'
        ],
        tabImage: [
            'flex',
            '!w-14',
            '!h-14',
            'items-start',
            'gap-spacing-spacing-10',
            'rounded-border-radius-radius-2',
            'overflow-hidden',
            'object-cover',
            'flex-shrink-0'
        ],
        tabText: [
            'flex',
            'flex-col',
            'text-left',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large',
            'flex-1',
            'min-w-0',
            'overflow-hidden',
            'text-ellipsis'
        ],
        contentWrapper: [
            'flex',
            'flex-1',
            'min-w-0',
            'lg:min-h-[474px]'
        ]
    },
    variants: {
        isActive: {
            true: {
                tabLink: [
                    'text-color-text-white',
                    'bg-component-button-primary-inverse-bg-hover',
                    'hover:bg-component-button-primary-inverse-bg-hover',
                    'hover:text-color-text-white',
                    'focus:bg-component-button-primary-inverse-bg-focused',
                    'focus:text-color-text-white',
                    'active:bg-component-button-primary-inverse-bg-hover',
                    'active:text-color-text-white'
                ]
            },
            false: {
                tabLink: [
                    'bg-color-grayscale-white',
                    'text-component-button-primary-text',
                    'hover:bg-component-button-primary-inverse-bg-hover',
                    'hover:text-color-text-white',
                    'focus:bg-component-button-primary-inverse-bg-focused',
                    'focus:text-color-text-white',
                    'active:bg-component-button-primary-inverse-bg-hover',
                    'active:text-color-text-white'
                ]
            }
        },
        disabled: {
            true: {
                tabLink: [
                    'opacity-50',
                    'bg-white',
                    'cursor-not-allowed'
                ]
            },
            false: {}
        },
        left: {
            true: {
                tabArrows: [
                    '-left-3',
                    'sm:-left-2',
                    'lg:hidden'
                ]
            },
            false: {}
        },
        right: {
            true: {
                tabArrows: [
                    'right-1',
                    'sm:right-2',
                    'lg:hidden'
                ]
            },
            false: {}
        }
    }
});
// Shared placeholder styling for service components
export const SHARED_PLACEHOLDER_VARIANTS = tv({
    slots: {
        imagePlaceholder: [
            'bg-color-grayscale-200',
            'rounded-border-radius-radius-2',
            'flex',
            'items-center',
            'justify-center'
        ],
        namePlaceholder: [
            'text-color-text-text-secondary',
            'text-typography-font-size-body-medium',
            'font-typography-font-family-body',
            'font-typography-font-weight-body-medium'
        ],
        titlePlaceholder: [
            'text-color-text-text-secondary',
            'text-typography-font-size-body-medium',
            'font-typography-font-family-body',
            'font-typography-font-weight-body-medium'
        ]
    },
    variants: {
        size: {
            full: {
                imagePlaceholder: [
                    'w-full',
                    'h-full'
                ]
            },
            fixed: {
                imagePlaceholder: [
                    'w-14',
                    'h-14'
                ]
            }
        },
        isActive: {
            true: {
                titlePlaceholder: [
                    'text-color-text-white',
                    'text-typography-font-size-body-medium',
                    'font-typography-font-family-body',
                    'font-typography-font-weight-body-medium'
                ]
            },
            false: {}
        }
    }
});
